// inpsections/components/NaplSection.js
// Section for the NAPL

import { useState, useEffect } from "react";
import styles from "./NaplSection.module.css";

const NaplSection = ({
  well,
  handleCheckboxChange,
  handleTextInputChange,
  handleTextInputOnBlur,
  handleNumericChange,
  handleNumericBlur,
  disableInspectionUpdates,
}) => {
  // Determine if LNAPL fields should be disabled
  const lnaplFieldsDisabled = !well.lnaplPresentInspection;

  // Determine if DNAPL fields should be disabled
  const dnaplFieldsDisabled = !well.dnaplPresentInspection;

  // Get field class name (using same class as other inspection controls)
  const getFieldClassName = (field) => {
    return styles.naplField;
  };

  return (
    <>
      {/* LNAPL Section */}
      <div className={styles.naplPresentRow}>
        <div className={styles.checkboxContainer}>
          <input
            type="checkbox"
            id={`lnaplPresentInspection-${well.id}`}
            name="lnaplPresentInspection"
            checked={well.lnaplPresentInspection || false}
            onChange={(e) =>
              handleCheckboxChange("lnaplPresentInspection", e.target.checked)
            }
            className={styles.largeCheckbox}
            disabled={disableInspectionUpdates}
          />
          <label
            htmlFor={`lnaplPresentInspection-${well.id}`}
            className={styles.naplCheckboxLabel}
          >
            LNAPL Present
          </label>
        </div>
      </div>

      <div className={styles.naplDataRow}>
        <div className={styles.naplField}>
          <label htmlFor={`lnaplThicknessInspection-${well.id}`}>
            LNAPL Thickness (ft):
          </label>
          <input
            type="number"
            inputMode="decimal"
            id={`lnaplThicknessInspection-${well.id}`}
            name="lnaplThicknessInspection"
            value={well.lnaplThicknessInspection || ""}
            onChange={(e) =>
              handleNumericChange("lnaplThicknessInspection", e.target.value)
            }
            onBlur={(e) =>
              handleNumericBlur("lnaplThicknessInspection", e.target.value)
            }
            onKeyDown={(e) => {
              // Prevent non-numeric entries
              if (
                e.key === "-" ||
                e.key === "e" ||
                e.key === "E" ||
                e.key === "+"
              ) {
                e.preventDefault();
              }
            }}
            className={getFieldClassName("lnaplThicknessInspection")}
            placeholder="0.00"
            disabled={lnaplFieldsDisabled || disableInspectionUpdates}
          />
        </div>

        <div className={styles.naplField}>
          <label htmlFor={`lnaplOdorInspection-${well.id}`}>Odor:</label>
          <input
            type="text"
            id={`lnaplOdorInspection-${well.id}`}
            name="lnaplOdorInspection"
            value={well.lnaplOdorInspection || ""}
            onChange={(e) =>
              handleTextInputChange("lnaplOdorInspection", e.target.value)
            }
            onBlur={(e) =>
              handleTextInputOnBlur("lnaplOdorInspection", e.target.value)
            }
            className={getFieldClassName("lnaplOdorInspection")}
            placeholder="Odor"
            disabled={lnaplFieldsDisabled || disableInspectionUpdates}
          />
        </div>

        <div className={styles.naplField}>
          <label htmlFor={`lnaplColorInspection-${well.id}`}>Color:</label>
          <input
            type="text"
            id={`lnaplColorInspection-${well.id}`}
            name="lnaplColorInspection"
            value={well.lnaplColorInspection || ""}
            onChange={(e) =>
              handleTextInputChange("lnaplColorInspection", e.target.value)
            }
            onBlur={(e) =>
              handleTextInputOnBlur("lnaplColorInspection", e.target.value)
            }
            className={getFieldClassName("lnaplColorInspection")}
            placeholder="Color"
            disabled={lnaplFieldsDisabled || disableInspectionUpdates}
          />
        </div>

        <div className={styles.naplField}>
          <label htmlFor={`lnaplOtherInspection-${well.id}`}>Other:</label>
          <input
            type="text"
            id={`lnaplOtherInspection-${well.id}`}
            name="lnaplOtherInspection"
            value={well.lnaplOtherInspection || ""}
            onChange={(e) =>
              handleTextInputChange("lnaplOtherInspection", e.target.value)
            }
            onBlur={(e) =>
              handleTextInputOnBlur("lnaplOtherInspection", e.target.value)
            }
            className={getFieldClassName("lnaplOtherInspection")}
            placeholder="Other observations"
            disabled={lnaplFieldsDisabled || disableInspectionUpdates}
          />
        </div>
      </div>

      {/* DNAPL Section */}
      <div className={styles.naplPresentRow}>
        <div className={styles.checkboxContainer}>
          <input
            type="checkbox"
            id={`dnaplPresentInspection-${well.id}`}
            name="dnaplPresentInspection"
            checked={well.dnaplPresentInspection || false}
            onChange={(e) =>
              handleCheckboxChange("dnaplPresentInspection", e.target.checked)
            }
            className={styles.largeCheckbox}
            disabled={disableInspectionUpdates}
          />
          <label
            htmlFor={`dnaplPresentInspection-${well.id}`}
            className={styles.naplCheckboxLabel}
          >
            DNAPL Present
          </label>
        </div>
      </div>

      <div className={styles.naplDataRow}>
        <div className={styles.naplField}>
          <label htmlFor={`dnaplThicknessInspection-${well.id}`}>
            DNAPL Thickness (ft):
          </label>
          <input
            type="number"
            inputMode="decimal"
            id={`dnaplThicknessInspection-${well.id}`}
            name="dnaplThicknessInspection"
            value={well.dnaplThicknessInspection || ""}
            onChange={(e) =>
              handleNumericChange("dnaplThicknessInspection", e.target.value)
            }
            onBlur={(e) =>
              handleNumericBlur("dnaplThicknessInspection", e.target.value)
            }
            onKeyDown={(e) => {
              // Prevent non-numeric entries
              if (
                e.key === "-" ||
                e.key === "e" ||
                e.key === "E" ||
                e.key === "+"
              ) {
                e.preventDefault();
              }
            }}
            className={getFieldClassName("dnaplThicknessInspection")}
            placeholder="0.00"
            disabled={dnaplFieldsDisabled || disableInspectionUpdates}
          />
        </div>

        <div className={styles.naplField}>
          <label htmlFor={`dnaplOdorInspection-${well.id}`}>Odor:</label>
          <input
            type="text"
            id={`dnaplOdorInspection-${well.id}`}
            name="dnaplOdorInspection"
            value={well.dnaplOdorInspection || ""}
            onChange={(e) =>
              handleTextInputChange("dnaplOdorInspection", e.target.value)
            }
            onBlur={(e) =>
              handleTextInputOnBlur("dnaplOdorInspection", e.target.value)
            }
            className={getFieldClassName("dnaplOdorInspection")}
            placeholder="Odor"
            disabled={dnaplFieldsDisabled || disableInspectionUpdates}
          />
        </div>

        <div className={styles.naplField}>
          <label htmlFor={`dnaplColorInspection-${well.id}`}>Color:</label>
          <input
            type="text"
            id={`dnaplColorInspection-${well.id}`}
            name="dnaplColorInspection"
            value={well.dnaplColorInspection || ""}
            onChange={(e) =>
              handleTextInputChange("dnaplColorInspection", e.target.value)
            }
            onBlur={(e) =>
              handleTextInputOnBlur("dnaplColorInspection", e.target.value)
            }
            className={getFieldClassName("dnaplColorInspection")}
            placeholder="Color"
            disabled={dnaplFieldsDisabled || disableInspectionUpdates}
          />
        </div>

        <div className={styles.naplField}>
          <label htmlFor={`dnaplOtherInspection-${well.id}`}>Other:</label>
          <input
            type="text"
            id={`dnaplOtherInspection-${well.id}`}
            name="dnaplOtherInspection"
            value={well.dnaplOtherInspection || ""}
            onChange={(e) =>
              handleTextInputChange("dnaplOtherInspection", e.target.value)
            }
            onBlur={(e) =>
              handleTextInputOnBlur("dnaplOtherInspection", e.target.value)
            }
            className={getFieldClassName("dnaplOtherInspection")}
            placeholder="Other observations"
            disabled={dnaplFieldsDisabled || disableInspectionUpdates}
          />
        </div>
      </div>
    </>
  );
};

export default NaplSection;
